<template>
    <div class="min-w-[1024px]">
        <div class="flex justify-between items-start">
            <div>
                <h3 class="font-inter-heading-10">Sub-admin ({{ subAdminOverviewList.length }})</h3>
            </div>

            <div 
                class="flex items-center gap-2.5"
            >
                <DesignButton
                    btnSize="MD"
                    btnType="QUATERNARY"
                    :btnDisabled="false"
                    btnText="View History"
                    @click-event="showHistoryModal"
                />

                <DesignButton
                    btnSize="MD"
                    btnType="PRIMARY"
                    :btnDisabled="false"
                    btnText="Add Sub-admin"
                    @click-event="onClickAddSubAdmin"
                >
                    <template #front-icon>
                        <AddIcon :width="16" :height="16" color="text-white" weight="FILL" />
                    </template>
                </DesignButton>
            </div>
        </div>

        <div v-if="subAdminOverviewList.length > 0 && unassignedDepartments.length > 0" 
            class="mt-5 p-4 bg-accent-red-1 border border-accent-red-1 rounded-02 flex items-start justify-between gap-4">
            <div class="flex items-start gap-3">
                <WarningIcon :width="24" :height="24" color="text-accent-red-3" weight="FILL" />
                <div class="flex flex-col gap-1">
                    <div class="font-inter-heading-19 text-content-basic-1">Notice</div>
                    <div class="font-inter-body-3 text-content-basic-1">
                        {{ unassignedDepartments.length }} departments have not been assigned to a sub-admin.<br />
                        Please assign these departments to a sub-admin.
                    </div>
                </div>
            </div>
            <div class="self-center">
                <DesignButton
                    btnSize="MD"
                    btnType="RED_SOLID"
                    :btnDisabled="false"
                    btnText="View"
                    @click-event="onClickViewUnassigned"
                >
                    <template #back-icon>
                        <ChevronRightIcon :width="16" :height="16" color="text-white" weight="FILL" />
                    </template>
                </DesignButton>
            </div>
        </div>

        <div class="mt-5 flex items-center gap-3">
            <div class="flex items-center gap-2">
                <DesignDropdownSelect
                    v-model="sortBy"
                    :options="sortByOptions"
                    placeholder="Sort by"
                    :readonly="subAdminOverviewList.length === 0"
                    size="LG"
                    width="w-52"
                    @change="sortFilterChange"
                />
            </div>
            <div class="flex items-center gap-2">
                <DesignDropdownSelect
                    v-model="order"
                    :options="orderOptions"
                    placeholder="Order"
                    :readonly="subAdminOverviewList.length === 0 || !sortBy"
                    size="LG"
                    width="w-52"
                    @change="sortFilterChange"
                />
            </div>
        </div>

        <div class="mt-5 bg-white border rounded-lg">
            <table class="w-full table-fixed">
                <colgroup>
                    <col style="width:11.5rem" />
                    <col style="width:7.7rem" />
                    <template v-for="i in Math.min(walletCategoryList.length, 5)" :key="'cg-'+i">
                        <col style="width:7.7rem" />
                    </template>
                    <col style="width:7.7rem" />
                    <col style="width:2rem" />
                </colgroup>
                <thead class="text-gray-600 bg-background-basic-2 rounded-t-lg">
                    <tr>
                        <th class="px-6 py-3 text-center font-inter-heading-19 rounded-tl-lg" rowspan="2">Name</th>
                        <th class="px-6 py-3 text-center font-inter-heading-19" rowspan="2">Assigned<br/>Department</th>
                        <th v-for="(cat, idx) in walletCategoryList.slice(0,5)" :key="'hc-'+(cat.walletCategorySeq||idx)" class="px-3 py-3 font-inter-heading-19">
                            <span class="block">Wallet {{ idx + 1 }}</span>
                            <span class="inline-block w-full max-w-[7rem] px-1 py-0.5 mt-1 rounded-01 text-center truncate border text-gray-600 border-border-basic-2 bg-white font-inter-caption-2">
                                {{ cat?.walletCategoryName || '-' }}
                            </span>
                        </th>
                        <th class="px-3 py-3 font-inter-heading-19" rowspan="2">Total Balance</th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-if="subAdminOverviewList.length === 0">
                        <td :colspan="4 + Math.min((walletCategoryList || []).length, 5)" class="py-16 text-center text-gray-400 font-inter-heading-20">
                            <div>There's no sub-admin.</div>
                            <div>Please click "+ Add Sub-admin" to set a sub-admin.</div>
                        </td>
                    </tr>
                    <tr v-else v-for="subAdminOverview in subAdminOverviewList" :key="subAdminOverview.subAdminSeq" class="border-t">
                        <td class="px-6 py-4 align-middle">
                            <div class="font-medium flex items-center gap-3 font-inter-heading-20">
                                <StatusCircle statusCode="ACTIVE" />
                                <div>
                                    <div class="line-clamp-2 break-words text-semibold">{{ subAdminOverview.subAdminName }}</div>
                                    <div class="font-inter-caption-2 text-gray-500 line-clamp-2 break-words">{{ subAdminOverview.subAdminDepartmentName }}</div>
                                </div>
                            </div>
                        </td>
                        <td class="font-inter-heading-20 px-6 py-4 align-middle">
                            <div class="flex justify-center items-center gap-2 h-full">
                                <button type="button" class="p-1 rounded hover:bg-background-basic-2 inline-flex items-center gap-2"
                                        @click="onClickViewAssignedDepartments(subAdminOverview)">
                                    <span>{{ subAdminOverview.assignedDepartmentCount }}</span>
                                    <ChevronRightIcon :width="16" :height="16" color="text-content-basic-2" weight="REGULAR" />
                                </button>
                            </div>
                        </td>
                        <td v-for="(cat, i) in walletCategoryList.slice(0,5)" :key="'r-'+subAdminOverview.subAdminSeq+'-'+i" class="px-3 py-4 align-middle text-center font-inter-heading-20">
                            {{ formatPoints(getAmountBySeq(subAdminOverview, cat.walletCategorySeq)) }}
                        </td>
                        <td class="font-inter-heading-20 px-3 py-4 align-middle text-center">{{ formatPoints(subAdminOverview.totalBalance) }}</td>
                        <td class="px-3 py-4 align-middle text-right pr-2">
                            <Menu as="div" class="relative inline-block text-left">
                                <div>
                                    <MenuButton class="inline-flex justify-center rounded-md p-2 hover">
                                        <MoreHorizIcon :width="24" :height="24" color="text-content-basic-2" weight="REGULAR" />
                                    </MenuButton>
                                </div>
                                <transition
                                    enter-active-class="transition ease-out duration-100"
                                    enter-from-class="transform opacity-0 scale-95"
                                    enter-to-class="transform opacity-100 scale-100"
                                    leave-active-class="transition ease-in duration-75"
                                    leave-from-class="transform opacity-100 scale-100"
                                    leave-to-class="transform opacity-0 scale-95">
                                    <MenuItems class="absolute right-0 mt-2 min-w-[104px] max-h-[400px] overflow-auto origin-top-right bg-background-basic-1 border border-border-basic-2 rounded-02 shadow-dropdown p-2 focus:outline-none z-50">
                                        <div class="flex flex-col gap-1">
                                            <MenuItem v-slot="{ active }">
                                                <button @click="onEditSubAdmin(subAdminOverview.subAdminSeq)" 
                                                        :class="['w-full text-left px-3 py-2 rounded-01_5 font-inter-body-2', active ? 'bg-background-basic-2 text-content-basic-2' : 'text-content-basic-2']">
                                                    Edit
                                                </button>
                                            </MenuItem>
                                            <MenuItem v-slot="{ active }">
                                                <button @click="onDeleteSubAdmin(subAdminOverview.subAdminSeq)" 
                                                        :class="['w-full text-left px-3 py-2 rounded-01_5 font-inter-body-2', active ? 'bg-background-basic-2 text-content-basic-2' : 'text-content-basic-2']">
                                                    Delete
                                                </button>
                                            </MenuItem>
                                        </div>
                                    </MenuItems>
                                </transition>
                            </Menu>
                        </td>
                    </tr>
                </tbody>
                <tfoot v-if="subAdminOverviewList.length > 0">
                    <tr class="text-blue-600 border-t">
                        <td class="font-inter-heading-20 px-5 py-3 text-center" colspan="1">Total</td>
                        <td class="font-inter-heading-20 px-5 py-3 text-center" colspan="1"></td>
                        <td v-for="(cat, i) in walletCategoryList.slice(0,5)" :key="'t-'+(cat.walletCategorySeq||i)" class="px-3 py-3 text-center">
                            {{ formatPoints(walletTotals[i]) }}</td>
                        <td class="font-inter-heading-20 px-3 py-3 text-center">{{ formatPoints(grandTotal) }}</td>
                        <td></td>
                    </tr>
                </tfoot>
            </table>
        </div>

        <Teleport to="body">
            <DesignViewHistoryModal
                v-model="visibleHistoryModal"
                :featureName="subAdminFeatureName"
                :contentsPerPage="10"
                :pageSize="9"
            />

            <DeletePopup
                v-model="showDeletePopup"
                title="Delete Confirmation"
                content="Deleting this wallet will return all Sub-admin points to the Admin account. (Points already distributed to employees will not be returned.)"
                checkboxText="Yes, I confirm to delete this wallet."
                @button-event="deleteSubAdmin(deleteSubAdminSeq)"
            />
            <AppPopup
                v-model="showAssignDepartmentPopup"
                name="Assign Sub-admin"
                :buttonText="null"
                :width800="false"
                :noneOverflowAuto="false"
            >
                <div class="max-w-[600px]">
                    <div class="px-5 py-3 font-inter-heading-18 text-content-basic-1">Department</div>
                    <div v-for="(dep, index) in unassignedDepartments" :key="dep.departmentSeq"
                        class="px-5 py-3 flex items-center justify-between"
                        :class="{ 'bg-background-basic-2': index % 2 === 0, 'bg-background-basic-1': index % 2 === 1 }">
                        <div class="font-inter-heading-19 text-content-basic-1 truncate">
                            {{ dep.departmentName }}
                        </div>
                        <DesignButton
                            class="!bg-transparent !border !border-primary-3 !text-primary-3 hover:!bg-primary-1"
                            btnSize="MD"
                            btnType="QUATERNARY"
                            :btnDisabled="false"
                            btnText="Assign"
                            @click-event="onClickAssignDepartment(dep.departmentSeq)"
                        />
                    </div>
                </div>
            </AppPopup>

            <AppPopup
                v-model="showAssignSubAdminPopup"
                name="Select Sub-admin"
                :buttonText="null"
                :width800="false"
                :noneOverflowAuto="false"
            >
                <div class="max-w-[600px]">
                    <div class="px-5 py-3 font-inter-heading-18 text-content-basic-1">Sub-admin List</div>
                    <div v-for="subAdmin in subAdminOverviewList" :key="subAdmin.subAdminSeq"
                        class="px-5 py-3 flex items-center gap-2 cursor-pointer"
                        :class="[{ 'bg-accent-blue-1': selectedSubAdminSeq === subAdmin.subAdminSeq }]"
                        @click="selectedSubAdminSeq = subAdmin.subAdminSeq"
                    >
                        <input type="radio" :value="subAdmin.subAdminSeq" v-model="selectedSubAdminSeq"
                                class="h-4 w-4 cursor-pointer rounded-full appearance-none border border-gray-300 bg-white transition-none
                                checked:bg-accent-blue-3 checked:border-transparent
                                focus:bg-accent-blue-3 focus:checked:bg-accent-blue-3 focus:border-transparent focus:outline-none focus:ring-0 focus:ring-offset-0
                                active:ring-0 active:bg-accent-blue-3 shadow-none"/>
                        <div class="font-inter-heading-19 text-content-basic-1 truncate">{{ subAdmin.subAdminName }}</div>
                    </div>
                    <div class="border-t px-5 py-3 flex justify-end gap-2">
                        <DesignButton
                            class="w-28"
                            btnType="QUATERNARY"
                            btnSize="MD"
                            :btnDisabled="false"
                            btnText="Previous"
                            @click-event="onClickPreviousAssignSubAdmin"
                        >
                            <template #front-icon>
                                <ChevronLeftIcon :width="16" :height="16" color="text-content-basic-1" weight="REGULAR" />
                            </template>
                        </DesignButton>
                        <DesignButton
                            class="w-28"
                            btnType="PRIMARY"
                            btnSize="MD"
                            :btnDisabled="false"
                            btnText="Assign"
                            @click-event="onClickAssignSubAdmin(selectedSubAdminSeq)"
                        />
                    </div>
                </div>
            </AppPopup>

            <DesignConfirmPopup
                v-model="showAssignConfirm"
                :showTitle="true"
                title="Confirm"
                :showCancel="true"
                cancelText="Cancel"
                :hasAfterConfirm="true"
                buttonText="Yes"
                :confirmDescription="`Are you sure you want to assign “${selectedDepartmentName}” to ${selectedSubAdminName}?`"
                @after-confirm="confirmAssignSelected"
                @cancel="showAssignConfirm = false"
            />

            <!-- Assigned Departments Popup -->
            <AppPopup
                v-model="showAssignedDeptPopup"
                :name="`Assigned Departments - ${popupSubAdminName}`"
                :buttonText="null"
                :width800="false"
                :noneOverflowAuto="false"
            >
                <div class="max-w-[600px]">
                    <div v-if="((popupSubAdmin?.assignedDepartments?.length) || 0) === 0" class="px-5 py-6 text-content-basic-3">
                        No departments assigned.
                    </div>
                    <div v-else v-for="(dep, index) in (popupSubAdmin?.assignedDepartments || [])"
                        :key="index"
                        class="px-5 py-3 flex items-center"
                        :class="{ 'bg-background-basic-2': index % 2 === 0, 'bg-background-basic-1': index % 2 === 1 }">
                        <div class="font-inter-heading-19 text-content-basic-1 truncate">
                            {{ dep.assignedDepartmentName }}
                        </div>
                    </div>
                </div>
            </AppPopup>
        </Teleport>
    </div>
</template>

<script>
import DesignDropdownSelect from '@/components/design-system/design-dropdown-select.vue';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import DeletePopup from '@/components/DeletePopup.vue';
import DesignConfirmPopup from '@/components/design-system/design-confirm-popup.vue';
import DesignViewHistoryModal from '@/components/design-system/design-view-history-modal.vue';
import AppPopup from '@/components/AppPopup.vue';
import DesignButton from '@/components/design-system/design-button.vue';
import AddIcon from '@/components/design-system/icon/add.vue';
import WarningIcon from '@/components/design-system/icon/warning.vue';
import ChevronRightIcon from '@/components/design-system/icon/chevron-right.vue';
import ChevronLeftIcon from '@/components/design-system/icon/chevron-left.vue';
import MoreHorizIcon from '@/components/design-system/icon/more-horiz.vue';
import StatusCircle from '@/components/employee/status-circle/status-circle.vue';
import { featureName } from '@/assets/data/optionData';

export default {
    components: {
        DesignDropdownSelect,
        Menu,
        MenuButton,
        MenuItem,
        MenuItems,
        DeletePopup,
        DesignConfirmPopup,
        DesignViewHistoryModal,
        AppPopup,
        DesignButton,
        AddIcon,
        WarningIcon,
        ChevronRightIcon,
        ChevronLeftIcon,
        MoreHorizIcon,
        StatusCircle,
    },
    data() {
        return {
            sortBy: null,
            order: null,
            subAdminOverviewList: [],
            walletCategoryList: [],
            unassignedDepartments: [],
            sortByOptions: [
                { text: 'Name', value: 'NAME' },
                { text: 'Assigned Teams', value: 'ASSIGNED_TEAMS' },
                { text: 'Total Balance', value: 'TOTAL_BALANCE' },
            ],
            orderOptions: [
                { text: 'Ascending', value: 'ASC' },
                { text: 'Descending', value: 'DESC' },
            ],
            deleteSubAdminSeq: null,
            showDeletePopup: false,
            
            showAssignDepartmentPopup: false,
            showAssignSubAdminPopup: false,
            showAssignConfirm: false,
            selectedSubAdminSeq: null,
            selectedSubAdminName: '',
            selectedAssignDepartmentSeq: null,
            selectedDepartmentName: '',
            showAssignedDeptPopup: false,
            popupSubAdminName: '',
            popupSubAdmin: null,
            visibleHistoryModal: false,
            scrollYBackup: 0,
        };
    },
    mounted() {
        this.getWalletCategoryList();
        this.getSubAdminOverview();
        this.getUnassignedDepartmentList();
    },
    computed: {
        subAdminFeatureName() {
            return featureName.SUB_ADMIN;
        },
        walletTotals() {
            const cats = this.walletCategoryList.slice(0, 5);
            return cats.map((cat) =>
                this.subAdminOverviewList.reduce(
                    (sum, row) => sum + (Number(this.getAmountBySeq(row, cat.walletCategorySeq)) || 0),
                    0
                )
            );
        },
        grandTotal() {
            return this.walletTotals.reduce((a, b) => a + b, 0);
        },
        // 여기에 모달이 더 있으면 || 로 추가
        isAnyPopupOpen() {
            return this.showAssignDepartmentPopup || this.showAssignSubAdminPopup || this.showAssignedDeptPopup 
            || this.showAssignConfirm || this.showDeletePopup || this.visibleHistoryModal;
        },
    },
    watch: {
        isAnyPopupOpen(val) {
            if (val) {
                this.lockBodyScroll();
            } else {
                this.unlockBodyScroll();
            }
        },
    },
    methods: {
        lockBodyScroll() {
            // 현재 스크롤 위치 저장
            this.scrollYBackup = window.scrollY || document.documentElement.scrollTop || 0;

            // 레이아웃 점프 방지 및 스크롤 잠금
            document.body.style.position = 'fixed';
            document.body.style.top = `-${this.scrollYBackup}px`;
            document.body.style.left = '0';
            document.body.style.right = '0';
            document.body.style.width = '100%';
            document.body.style.overflow = 'hidden'; // 보조(데스크톱)
        },
        unlockBodyScroll() {
            // 원복
            const y = this.scrollYBackup || 0;
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.left = '';
            document.body.style.right = '';
            document.body.style.width = '';
            document.body.style.overflow = '';

            // 저장해 둔 위치로 복귀
            window.scrollTo(0, y);
        },
        showHistoryModal() {
            this.visibleHistoryModal = true;
        },
        onClickAddSubAdmin() {
            window.location.href = '/flex_ben/flex_ben_setting/subAdmin/add_sub_admin';
        },
        formatCurrency(v) {
            if (v == null) return '-';
            try { return new Intl.NumberFormat().format(v); } catch { return String(v); }
        },
        formatPoints(v) {
            if (v == null || isNaN(v)) return '-';
            try { return new Intl.NumberFormat().format(v) + 'P'; } catch { return v + 'P'; }
        },
        getWalletCategoryList() {
            const self = this;
            const url = self.$api('uri', 'get-wallet-category-list');
            self.$axios
                .get(url)
                .then((res) => {
                    self.walletCategoryList = res.data?.data?.list ?? [];
                })
                .catch((err) => {
                    console.error('err : ', err);
                });
        },
        getSubAdminOverview() {
            const self = this;
            const url = self.$api('uri', 'get-sub-admin-overview');
            return self.$axios
                .get(url, { params: { sortBy: self.sortBy, order: self.order } })
                .then((res) => {
                    self.subAdminOverviewList = res.data?.data?.list ?? [];     
                })
                .catch((err) => {
                    console.error('getSubAdminOverview error', err);
                });
        },
        getAmountBySeq(item, walletCategorySeq) {
            const wallets = Array.isArray(item?.wallets) ? item.wallets : [];
            const w = wallets.find(v => v?.walletCategorySeq === walletCategorySeq);
            return w ? (Number(w.walletSubAdminMileage) || 0) : 0;
        },  
        sortFilterChange() {
            const self = this;
            if (self.sortBy == null || self.order == null) {
                return;
            }
            self.getSubAdminOverview();
        },
        getUnassignedDepartmentList() {
            const url = this.$api('uri', 'get-department-unassigned-sub-admin');
            this.$axios
                .get(url)
                .then((res) => {
                    const data = res.data?.data;
                    const list = Array.isArray(data?.list) ? data.list : Array.isArray(data) ? data : [];
                    this.unassignedDepartments = list;
                })
                .catch((err) => {
                    
                });
        },
        deleteSubAdmin(subAdminSeq) {
            const url = this.$api('uri', 'delete-sub-admin').replace('{subAdminSeq}', subAdminSeq);
            this.$axios
                .delete(url)
                .then(() => {
                    this.showDeletePopup = false;
                    this.getSubAdminOverview();
                })
                .catch((err) => {
                    this.showDeletePopup = false;
                    console.error('deleteSubAdmin error', err);
                });
        },
        onClickViewUnassigned() {
            this.showAssignDepartmentPopup = true;
        },
        onClickAssignDepartment(departmentSeq) {
            this.selectedAssignDepartmentSeq = departmentSeq;
            this.selectedDepartmentName = this.unassignedDepartments.find(dep => dep.departmentSeq === departmentSeq)?.departmentName || '';
            const first = (Array.isArray(this.subAdminOverviewList) && this.subAdminOverviewList.length > 0)
                ? this.subAdminOverviewList[0]
                : null;
            this.selectedSubAdminSeq = first ? first.subAdminSeq : null;
            this.selectedSubAdminName = first ? first.subAdminName : '';
            this.showAssignDepartmentPopup = false;
            this.showAssignSubAdminPopup = true;
        },
        onClickAssignSubAdmin(subAdminSeq) {
            if (!subAdminSeq) return;
            this.selectedSubAdminSeq = subAdminSeq;
            this.selectedSubAdminName = this.subAdminOverviewList.find(sa => sa.subAdminSeq === subAdminSeq)?.subAdminName || '';
            this.showAssignSubAdminPopup = false;
            this.showAssignConfirm = true;
        },
        onClickPreviousAssignSubAdmin() {
            this.selectedAssignDepartmentSeq = null;
            this.selectedDepartmentName = '';
            this.selectedSubAdminSeq = null;
            this.selectedSubAdminName = '';
            this.showAssignSubAdminPopup = false;
            this.showAssignDepartmentPopup = true;
        },
        confirmAssignSelected() {
            if (!this.selectedAssignDepartmentSeq || !this.selectedSubAdminSeq) {
                return;
            }

            const isLastDepartment = this.unassignedDepartments.length === 1;
            const url = this.$api('uri','put-sub-admin-department-item')
                .replace('{subAdminSeq}', this.selectedSubAdminSeq)
                .replace('{departmentSeq}', this.selectedAssignDepartmentSeq);

            this.$axios.put(url)
                .then(() => Promise.all([ this.getSubAdminOverview(), this.getUnassignedDepartmentList() ]))
                .then(() => {
                    isLastDepartment
                    ? this.showAssignDepartmentPopup = false
                    : this.showAssignDepartmentPopup = true;
                    
                })
                .catch(err => console.error('assign error', err))
                .finally(() => { this.showAssignConfirm = false; });
        },
        onEditSubAdmin(subAdminSeq) {
            if (!subAdminSeq) return;
            window.location.href = `/flex_ben/flex_ben_setting/subAdmin/edit_sub_admin?subAdminSeq=${subAdminSeq}`;
        },
        onDeleteSubAdmin(subAdminSeq) {
            this.deleteSubAdminSeq = subAdminSeq;
            this.showDeletePopup = true;
        },
        onClickViewAssignedDepartments(subAdminOverview) {
            if (!subAdminOverview) return;
            this.popupSubAdminName = subAdminOverview.subAdminName || '';
            this.popupSubAdmin = subAdminOverview;
            this.showAssignedDeptPopup = true;
        },
    },
};
</script>

<style scoped>
.blu { background: #4361ee; }
.blu:hover { background: #334cba; }
</style>

